import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Save, FileText, Info, Layout } from 'lucide-react';
import { useTemplatesNew } from './hooks/useTemplatesNew';
import { cn } from '@/utils/cn';

export function TemplatesNewPage() {
  const navigate = useNavigate();
  const { formData, setFormData, handleNameChange, isSaving, createTemplate } = useTemplatesNew();

  return (
    <div className="w-full flex-1 min-w-0 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-100 bg-white p-3 shadow-sm">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('?tab=templates')}
            className="flex h-10 w-10 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 active:scale-95 transition-all"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-lg font-medium text-slate-900">Tạo mẫu mới</h1>
            <p className="text-sm text-slate-500">Thiết lập thông tin cơ bản cho mẫu tài liệu.</p>
          </div>
        </div>
        <button
          onClick={createTemplate}
          disabled={isSaving || !formData.name}
          className="flex h-10 items-center gap-2 rounded-md bg-emerald-700 px-4 text-sm font-medium text-white shadow-md shadow-emerald-700/10 hover:bg-emerald-800 disabled:opacity-50 transition-all active:scale-95"
        >
          <Save className="h-4 w-4" />
          <span>{isSaving ? 'Đang lưu...' : 'Lưu mẫu'}</span>
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        {/* Main Form */}
        <div className="md:col-span-2 space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-medium">
              <Info className="h-4 w-4 text-emerald-600" />
              <span>Thông tin chung</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Tên mẫu tài liệu</label>
                <input
                  type="text"
                  placeholder="Ví dụ: Báo cáo nghiệm thu thi công"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50/50 px-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Mã định danh (Slug)</label>
                <input
                  type="text"
                  placeholder="BAO-CAO-NGHIEM-THU"
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-100 px-3 text-sm font-mono text-slate-600 outline-none"
                  value={formData.code}
                  readOnly
                />
                <p className="text-[10px] text-slate-400 font-medium italic">* Mã này được tạo tự động để định danh hệ thống.</p>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Mô tả chi tiết</label>
                <textarea
                  rows={4}
                  placeholder="Nhập mô tả về mục đích sử dụng mẫu tài liệu này..."
                  className="w-full rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm outline-none focus:border-emerald-500 focus:bg-white transition-all"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Settings */}
        <div className="space-y-4">
          <div className="rounded-md border border-slate-100 bg-white p-4 shadow-sm space-y-4">
            <div className="flex items-center gap-2 border-b border-slate-50 pb-2 text-slate-800 font-medium">
              <Settings className="h-4 w-4 text-emerald-600" />
              <span>Cấu hình</span>
            </div>

            <div className="space-y-4">
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Phân loại</label>
                <div className="grid grid-cols-1 gap-2">
                  {[
                    { val: 'report', label: 'Báo cáo', icon: FileText },
                    { val: 'workflow', label: 'Quy trình', icon: Layout },
                    { val: 'form', label: 'Biểu mẫu', icon: Save },
                  ].map((item) => (
                    <button
                      key={item.val}
                      onClick={() => setFormData({ ...formData, type: item.val as any })}
                      className={cn(
                        'flex h-11 items-center gap-3 rounded-md border px-3 text-sm font-medium transition-all',
                        formData.type === item.val
                          ? 'border-emerald-500 bg-emerald-50 text-emerald-700 shadow-sm shadow-emerald-500/5'
                          : 'border-slate-100 bg-slate-50 text-slate-500 hover:border-slate-200 hover:bg-white'
                      )}
                    >
                      <item.icon className={cn('h-4 w-4', formData.type === item.val ? 'text-emerald-600' : 'text-slate-400')} />
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-500 uppercase tracking-wider">Trạng thái ban đầu</label>
                <select
                  className="h-11 w-full rounded-md border border-slate-200 bg-slate-50 px-3 text-sm font-medium outline-none focus:border-emerald-500 transition-all"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="active">Công khai ngay</option>
                  <option value="draft">Lưu nháp</option>
                </select>
              </div>
            </div>
          </div>

          <div className="rounded-md bg-emerald-50 p-4 border border-emerald-100">
            <h4 className="text-xs font-medium text-emerald-800 mb-1">Gợi ý thiết lập</h4>
            <p className="text-xs text-emerald-600 leading-relaxed">
              Bạn nên đặt tên mẫu rõ ràng và gắn mã định danh chuẩn để dễ dàng phân tích và báo cáo sau này.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
